import React, { useCallback, useEffect, useState } from "react";
import { useSite } from "../lib/SiteContext";
import api from "../lib/api";
import ConnectorSetupModal from "../components/ConnectorSetupModal";
import ConnectorSummary from "../components/ConnectorSummary";
import DataSourceList from "../components/data-sources/DataSourceList";
import SyncRunsList from "../components/data-sources/SyncRunsList";
import UpgradePath from "../components/data-sources/UpgradePath";
import { Database } from "lucide-react";

const PROVIDER_LABELS = {
  homewizard: "HomeWizard P1",
  fusionsolar: "FusionSolar",
  open_meteo: "Open-Meteo",
  wunderground: "Wunderground",
  manual: "Manual entry",
  csv: "CSV import",
  bridge: "Local bridge",
  other: "Other",
};
const CONNECTOR_LABELS = {
  manual: "Manual",
  csv_import: "CSV import",
  mobile_local_sync: "Mobile local sync",
  bridge: "Local bridge",
  cloud_api: "Cloud API",
};
const DOMAIN_COLOR = {
  energy: "#F59E0B", solar: "#F59E0B",
  water: "#3B82F6", weather: "#A1A1AA",
  device: "#A1A1AA", battery: "#10B981",
};

export default function DataSources() {
  const { currentSite } = useSite();
  const [sources, setSources] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState(null);
  const [configureSource, setConfigureSource] = useState(null);

  const reload = useCallback(async () => {
    if (!currentSite) return;
    setLoading(true);
    const [sourcesRes, runsRes] = await Promise.all([
      api.get(`/sites/${currentSite.id}/data-sources`),
      api.get(`/sites/${currentSite.id}/sync-runs?limit=15`),
    ]);
    setSources(sourcesRes.data || []);
    setRuns(runsRes.data || []);
    setLoading(false);
  }, [currentSite]);
  useEffect(() => { reload(); }, [reload]);

  const sync = async (ds) => {
    setSyncingId(ds.id);
    try {
      if (ds.provider === "open_meteo") {
        await api.post(`/sites/${currentSite.id}/weather/sync?days=30`);
      } else if (ds.provider === "wunderground") {
        await api.post(
          `/sites/${currentSite.id}/wunderground/sync?data_source_id=${ds.id}&days=30`,
        );
      } else {
        return;
      }
      await reload();
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <div className="space-y-8 pb-12" data-testid="data-sources-page">
      <header>
        <div className="label-tiny mb-1.5 flex items-center gap-2"><Database className="w-3.5 h-3.5" /> Data Sources</div>
        <h1 className="font-heading text-3xl font-semibold">Where your data comes from</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Mix and match how each part of your home reports in — drag-and-drop a spreadsheet, let your phone sync on the home network, install a small bridge, or plug into a cloud API.
        </p>
      </header>

      <UpgradePath />

      <ConnectorSummary sources={sources} />

      <DataSourceList
        sources={sources}
        loading={loading}
        syncingId={syncingId}
        providerLabels={PROVIDER_LABELS}
        connectorLabels={CONNECTOR_LABELS}
        domainColor={DOMAIN_COLOR}
        onSync={sync}
        onConfigure={setConfigureSource}
      />

      <SyncRunsList runs={runs} providerLabels={PROVIDER_LABELS} loading={loading} />

      <ConnectorSetupModal
        open={!!configureSource}
        source={configureSource}
        currentSite={currentSite}
        onClose={() => setConfigureSource(null)}
        onUpdated={reload}
      />
    </div>
  );
}
